import {SearchIcon} from '@primer/octicons-react'
import {Autocomplete, Box, FormControl, Select, TextInput} from '@primer/react'

import {forwardRef, useCallback, useMemo, useState} from 'react'
import {DragAndDropMoveOptions} from '../utils/types'
import {testIdProps} from '@github-ui/test-id-props'

type AutocompleteItem = {text: string; id: string}

interface InputProps {
  options: Array<{title: string; id: string | number}>
  dropdownSelection: string | undefined
  onChange: (index: number) => void
  setDropdownSelection: (selection: DragAndDropMoveOptions) => void
  initialPosition: number
}

const INPUT_LABEL = 'move-modal-input-label'

const isValidBeforeMove = (index: number, initialIndex: number) => {
  return index !== initialIndex + 1 && index !== initialIndex
}

const isValidAfterMove = (index: number, initialIndex: number) => {
  return index !== initialIndex - 1 && index !== initialIndex
}

/**
 * A form that allows the user to move an item to a new position in the list.
 *
 * @param options The list of items to move
 * @param dropdownSelection The current dropdown selection
 * @param onChange Callback to update the index of the item being moved
 * @param setDropdownSelection Callback to update the dropdown selection
 * @param initialPosition The initial position of the item being moved
 */
export const MoveModalForm = forwardRef<HTMLInputElement, InputProps>(function NameInput(
  {onChange: onChangeNumber, setDropdownSelection, dropdownSelection, options, initialPosition},
  ref,
) {
  const [filterValue, setFilterValue] = useState('')

  const autocompleteTemplateList = useMemo(() => {
    const initialIndex = initialPosition - 1

    const beforeOptions: AutocompleteItem[] = []
    const afterOptions: AutocompleteItem[] = []

    for (let index = 0; index < options.length; index++) {
      /* Checks if */
      if (isValidBeforeMove(index, initialIndex)) {
        beforeOptions.push({
          text: options[index]?.title || '',
          id: `${index + 1}`,
        })
      }

      if (isValidAfterMove(index, initialIndex)) {
        afterOptions.push({
          text: options[index]?.title || '',
          id: `${index + 1}`,
        })
      }
    }
    return {before: beforeOptions, after: afterOptions}
  }, [initialPosition, options])

  const [selectedAutocomplete, setSelectedAutocomplete] = useState<AutocompleteItem>({
    text: '',
    id: '0',
  })

  const onChange = useCallback(
    (value: string | number) => {
      const parsedValue = typeof value === 'string' ? parseInt(value, 10) : value
      onChangeNumber(parsedValue - 1)
    },
    [onChangeNumber],
  )

  const autocompleteSelection = useCallback(
    (selectedItem: AutocompleteItem) => {
      setSelectedAutocomplete(selectedItem)
      onChange(selectedItem.id.toString())
    },
    [onChange],
  )

  const fuzzySearchFilter = (item: AutocompleteItem) => {
    return item.text.toLowerCase().includes(filterValue.toLowerCase())
  }

  const onSelectedChange = (selected: AutocompleteItem | AutocompleteItem[]) => {
    if (!Array.isArray(selected)) {
      return
    }

    const newlySelectedArray = selected.filter(function (item) {
      if (item?.id) {
        return selectedAutocomplete.id !== item.id
      }
    })

    if (newlySelectedArray[0]) {
      autocompleteSelection(newlySelectedArray[0])
    }
  }

  return (
    <Box sx={{display: 'flex', width: '100%', flexDirection: 'column', gap: 3}}>
      <FormControl required>
        <FormControl.Label>Action</FormControl.Label>
        <Select
          block
          onChange={e => {
            setDropdownSelection(e.currentTarget.value as DragAndDropMoveOptions)
            onChange(initialPosition)
          }}
        >
          {Object.values(DragAndDropMoveOptions).map(item => (
            <Select.Option key={item} value={item}>
              {item}
            </Select.Option>
          ))}
        </Select>
      </FormControl>
      <FormControl required>
        <FormControl.Label id={INPUT_LABEL}>{dropdownSelection}</FormControl.Label>
        {dropdownSelection === DragAndDropMoveOptions.ROW ? (
          <TextInput
            block
            min={1}
            max={options.length}
            defaultValue={initialPosition}
            type="number"
            onChange={ev => onChange(ev.target.value)}
            ref={ref}
            {...testIdProps('drag-and-drop-move-modal-position-input')}
          />
        ) : (
          <Autocomplete>
            <Autocomplete.Input
              placeholder={'Position'}
              value={selectedAutocomplete.text}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFilterValue(event.target.value)
              }}
              leadingVisual={SearchIcon}
              sx={{width: '100%'}}
              {...testIdProps('drag-and-drop-move-modal-position-input')}
            />
            <Autocomplete.Overlay sx={{overflow: 'auto', maxHeight: '200px'}}>
              <Autocomplete.Menu
                items={
                  autocompleteTemplateList[dropdownSelection === DragAndDropMoveOptions.BEFORE ? 'before' : 'after']
                }
                aria-labelledby={INPUT_LABEL}
                selectedItemIds={[selectedAutocomplete.id.toString()]}
                onSelectedChange={onSelectedChange}
                filterFn={fuzzySearchFilter}
              />
            </Autocomplete.Overlay>
          </Autocomplete>
        )}
      </FormControl>
    </Box>
  )
})

try{ MoveModalForm.displayName ||= 'MoveModalForm' } catch {}